// Synthetic scenario data — drives the scenario engine in the concept walkthrough.
// Each scenario carries severity-indexed conditions so the panel updates with the slider.
// Severity scale: 1=low, 2=elevated, 3=high, 4=critical.

export const SCENARIOS = [
  {
    id: "urban-flooding",
    name: "Urban Flooding & Transport Disruption",
    locale: "Greater Manchester · UK",
    summary:
      "Sustained rainfall combined with overtopped culverts is producing surface water flooding across central wards. Bus diversions in effect; tram service degraded.",
    affectedZones: ["Zone A — City Centre", "Zone B — Northern Quarter", "Zone C — Salford Crescent"],
    affectedGroups: ["Commuters", "Care providers", "Schools", "SME deliveries"],
    services: ["Bus network", "Tram (Metrolink)", "Surface drainage", "Council comms"],
    conditions: {
      1: {
        headline: "Localised surface water on two arterial routes.",
        metrics: { rainfall_mm_hr: 6, river_level_m: 2.1, bus_delay_min: 4, schools_at_risk: 0 },
        nextStep: "Forecast trending steady. Light shower band passes within 90 min.",
      },
      2: {
        headline: "Multiple ward gullies overwhelmed; bus diversions live.",
        metrics: { rainfall_mm_hr: 14, river_level_m: 2.7, bus_delay_min: 12, schools_at_risk: 2 },
        nextStep: "Tidal lock at +2h likely to slow drainage. Tram speed restrictions probable.",
      },
      3: {
        headline: "Transport network degraded; care visit cancellations rising.",
        metrics: { rainfall_mm_hr: 22, river_level_m: 3.4, bus_delay_min: 28, schools_at_risk: 5 },
        nextStep: "EA flood warning expected within 60 min on River Irwell tributary.",
      },
      4: {
        headline: "Critical: river overtopping risk; cross-agency response active.",
        metrics: { rainfall_mm_hr: 31, river_level_m: 4.1, bus_delay_min: 55, schools_at_risk: 11 },
        nextStep: "Emergency Services Mobile Command standing up. Mutual aid being requested.",
      },
    },
  },
  {
    id: "heatwave",
    name: "Prolonged Heatwave & Grid Stress",
    locale: "South-East England",
    summary:
      "Day 4 of a Level-3 heat alert. Demand on cooling load is straining substation capacity. Vulnerable-resident outreach is underway.",
    affectedZones: ["Zone A — Coastal towns", "Zone B — Inner urban", "Zone C — Care home cluster"],
    affectedGroups: ["Over-75s", "Outdoor workers", "Schools (no AC)", "Care homes"],
    services: ["Distribution network", "Adult social care", "Public health comms", "Cooling centres"],
    conditions: {
      1: {
        headline: "Warm but stable. Wellbeing checks proceeding on schedule.",
        metrics: { peak_temp_c: 28, grid_load_pct: 71, vulnerable_unreached: 9, cooling_centres_open: 3 },
        nextStep: "Forecast stable for 24h. Routine outreach continues.",
      },
      2: {
        headline: "Elevated discomfort; substation A near upper limit.",
        metrics: { peak_temp_c: 32, grid_load_pct: 84, vulnerable_unreached: 24, cooling_centres_open: 5 },
        nextStep: "Demand-response request likely overnight. Care provider workload climbing.",
      },
      3: {
        headline: "Heat-health alert level 3; grid load capping.",
        metrics: { peak_temp_c: 35, grid_load_pct: 92, vulnerable_unreached: 47, cooling_centres_open: 7 },
        nextStep: "Possible managed brownouts in Zone B if load > 95% for 30 min.",
      },
      4: {
        headline: "Critical: hospital admissions rising; mutual aid being coordinated.",
        metrics: { peak_temp_c: 38, grid_load_pct: 97, vulnerable_unreached: 62, cooling_centres_open: 9 },
        nextStep: "DNO requesting demand-side response. NHS escalation expected.",
      },
    },
  },
  {
    id: "rail-incident",
    name: "Major Rail Incident — Mainline North",
    locale: "East Coast Mainline · UK",
    summary:
      "An overhead-line fault has stopped traffic between two major stations. Passenger welfare and onward-travel coordination required.",
    affectedZones: ["Section 12 — Doncaster→York", "Stations: Doncaster, York"],
    affectedGroups: ["In-service passengers", "Connecting services", "Station staff", "Replacement coach ops"],
    services: ["Mainline rail", "Station welfare", "Coach contracts", "Operator comms"],
    conditions: {
      1: {
        headline: "Localised signalling fault — single-line working established.",
        metrics: { trains_held: 2, pax_affected: 240, downstream_delay_min: 8, welfare_calls_open: 0 },
        nextStep: "Repair team ETA 25 min. Minor knock-on delays expected.",
      },
      2: {
        headline: "OHLE damage confirmed; both lines blocked for repair.",
        metrics: { trains_held: 5, pax_affected: 1100, downstream_delay_min: 35, welfare_calls_open: 3 },
        nextStep: "Replacement coach activation under consideration.",
      },
      3: {
        headline: "Extended block; passenger welfare situation at platform-level.",
        metrics: { trains_held: 9, pax_affected: 2400, downstream_delay_min: 75, welfare_calls_open: 11 },
        nextStep: "Operator-to-operator handoff for connecting services likely.",
      },
      4: {
        headline: "Critical: line out for >3h, passenger detrainment in progress.",
        metrics: { trains_held: 14, pax_affected: 4200, downstream_delay_min: 130, welfare_calls_open: 28 },
        nextStep: "Cross-operator mutual aid + national rail comms escalation.",
      },
    },
  },
];

export const SEVERITY_LABELS = {
  1: { label: "Low", tone: "low" },
  2: { label: "Elevated", tone: "elevated" },
  3: { label: "High", tone: "high" },
  4: { label: "Critical", tone: "critical" },
};
